import React, { useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { shouldShowGuidelinesConsentGate } from '../../util/signupConsent';
import { NamedLink, PrimaryButton } from '../../components';

import css from './GuidelinesConsentGate.module.css';

/**
 * One-time Community Guidelines prompt for existing accounts.
 * Shown after sign-in / on Dashboard. Never inside a live call.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.currentUser]
 * @param {string} [props.pathname]
 * @param {Function} props.onAccept
 * @returns {JSX.Element|null}
 */
const GuidelinesConsentGate = props => {
  const { currentUser, pathname, onAccept } = props;
  const [checked, setChecked] = useState(false);
  const [needed, setNeeded] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const uid = currentUser?.id?.uuid;
  let acceptedThisSession = false;
  try {
    acceptedThisSession =
      !!uid && typeof window !== 'undefined' && window.sessionStorage.getItem('sl_gl_accepted') === uid;
  } catch (e) {}

  // Hide right after acceptance, before currentUser refreshes (Sam, Oct 23).
  if (accepted || acceptedThisSession || !shouldShowGuidelinesConsentGate({ currentUser, pathname })) {
    return null;
  }

  const handleContinue = event => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (!checked) {
      setNeeded(true);
      return;
    }
    if (typeof onAccept !== 'function') {
      return;
    }
    setInProgress(true);
    setError(null);
    Promise.resolve(onAccept())
      .then(() => {
        try { window.sessionStorage.setItem('sl_gl_accepted', uid || ''); } catch (e) {}
        setAccepted(true);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setInProgress(false);
      });
  };

  return (
    <div
      className={css.root}
      data-testid="guidelines-consent-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guidelines-consent-gate-title"
    >
      <div className={css.card}>
        <h2 id="guidelines-consent-gate-title" className={css.title}>
          <FormattedMessage id="GuidelinesConsentGate.title" />
        </h2>
        <p className={css.body}>
          <FormattedMessage id="GuidelinesConsentGate.body" />
        </p>
        <label className={css.check} htmlFor="guidelines-consent-gate-agree">
          <input
            id="guidelines-consent-gate-agree"
            type="checkbox"
            checked={checked}
            onChange={event => {
              setChecked(event.target.checked);
              if (event.target.checked) {
                setNeeded(false);
              }
            }}
          />
          <span>
            <FormattedMessage id="GuidelinesConsentGate.agree" />
          </span>
        </label>
        <p className={css.readLinks}>
          <NamedLink
            name="SafetyPage"
            to={{ hash: 'community-guidelines' }}
            className={css.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FormattedMessage id="SignupForm.agreeGuidelinesLink" />
          </NamedLink>
        </p>
        {needed ? (
          <p className={css.error} role="alert">
            <FormattedMessage id="SignupForm.agreeGuidelinesRequired" />
          </p>
        ) : null}
        {error ? (
          <p className={css.error} role="alert">
            <FormattedMessage id="GuidelinesConsentGate.failed" />
          </p>
        ) : null}
        <PrimaryButton type="button" inProgress={inProgress} onClick={handleContinue}>
          <FormattedMessage id="GuidelinesConsentGate.continue" />
        </PrimaryButton>
      </div>
    </div>
  );
};

export default GuidelinesConsentGate;
